<template>
  <div>
    <!-- Filter Button -->
    <BaseFilterButton
      :name="'Date Range'"
      :active="isActive"
      :tagValue="tagValue"
      :menuOpen="isOpen"
      @click="isOpen = !isOpen"
    />

    <!-- Custom Modal Backdrop -->
    <Teleport to="body">
      <Transition name="fade">
        <div v-if="isOpen" class="date-filter-backdrop" @click.self="isOpen = false">
          <!-- Modal Dialog -->
          <div class="date-filter-modal">
            <div class="date-filter-modal__header">
              <h3>Select Date Range</h3>
              <button class="date-filter-modal__close" @click="isOpen = false">
                <span>&times;</span>
              </button>
            </div>

            <div class="date-filter-modal__content">
              <Transition name="fade" mode="out-in">
                <div v-if="initialized" class="date-container">
                  <div class="date-picker-wrapper">
                    <label>Start Date</label>
                    <v-date-picker
                      v-model="startDate"
                      :max="maxDateFormatted"
                      :min="minDateFormatted"
                      elevation="0"
                      color="#250E81"
                    />
                  </div>
                  <div class="date-picker-wrapper">
                    <label>End Date</label>
                    <v-date-picker
                      v-model="endDate"
                      :max="maxDateFormatted"
                      :min="minDateFormatted"
                      elevation="0"
                      color="#250E81"
                    />
                  </div>
                </div>
                <div v-else class="date-range-loader">
                  <v-skeleton-loader
                    type="date-picker-days"
                    theme="primary"
                    color="transparent"
                  />
                  <v-skeleton-loader
                    type="date-picker-days"
                    theme="primary"
                    color="transparent"
                  />
                </div>
              </Transition>
            </div>

            <div class="date-filter-modal__footer">
              <button class="button-text" @click="isOpen = false">Cancel</button>
              <button :disabled="!isActive" class="button-text clear-button" @click="handleClear">Clear Selection</button>
              <button class="button" @click="isOpen = false">Done</button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import BaseFilterButton from '@/components/BaseFilterButton.vue';
import * as utils from '@/assets/js/utils.js';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';

export default {
  name: "DateRangeFilter",
  components: {
    BaseFilterButton,
  },
  props: {
    modelValue: {
      type: Array,
      default: null,
    },
    dataset: {
      type: Object,
      default: () => {},
    },
    invert: {
      type: Boolean,
      default: false,
    },
    zIndex: {
      type: [String, Number],
      default: 499,
    },
  },
  emits: [
    'update:modelValue',
  ],
  data() {
    return {
      isOpen: false,
      startDate: null,
      endDate: null,
      minDate: null,
      maxDate: null,
      initialized: false,
    };
  },

  computed: {
    currentYear() {
      const currentDate = new Date();
      return currentDate.getFullYear();
    },
    isActive() {
      if (!this.startDate || !this.endDate || !this.minDate || !this.maxDate) {
        return false;
      }
      // Ensure that startDate and endDate are not null
      const startDateValid = this.startDate !== null;
      const endDateValid = this.endDate !== null;

      // Check if startDate and endDate are different from minDate and maxDate
      const startDateDifferent = startDateValid && this.startDate.getTime() !== this.minDate.getTime();
      const endDateDifferent = endDateValid && this.endDate.getTime() !== this.maxDate.getTime();

      // Return true if both startDate and endDate are set and different from minDate and maxDate
      return (startDateValid || endDateValid) && (startDateDifferent || endDateDifferent);
    },
    tagValue() {
      let startDate = '';
      let endDate = '';
      if (this.startDate) {
        startDate = new Date(this.startDate);
        startDate = `${startDate.getMonth() + 1}/${startDate.getDate()}/${startDate.getFullYear()}`;
      }

      if (this.endDate) {
        endDate = new Date(this.endDate);
        endDate = `${endDate.getMonth() + 1}/${endDate.getDate()}/${endDate.getFullYear()}`;
      }

      if (this.startDate || this.endDate) {
        return `${startDate} - ${endDate}`;
      }
      return '';
    },
    minDateFormatted() {
      if (!this.minDate) return undefined;
      const year = this.minDate.getUTCFullYear();
      const month = String(this.minDate.getUTCMonth() + 1).padStart(2, '0'); // Months are zero-indexed
      const day = String(this.minDate.getUTCDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      return formattedDate;
    },
    maxDateFormatted() {
      if (!this.maxDate) return undefined;
      const year = this.maxDate.getUTCFullYear();
      const month = String(this.maxDate.getUTCMonth() + 1).padStart(2, '0'); // Months are zero-indexed
      const day = String(this.maxDate.getUTCDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      return formattedDate;
    },
  },
  watch: {
    startDate() {
      this.debouncedUpdateFilters();
    },
    endDate() {
      this.debouncedUpdateFilters();
    },
    isOpen(isOpen) {
      if (isOpen) {
        const dataConnect = new DatastoreConnect();
        dataConnect.getDatasetDateRange({ dataset_id: this.dataset.id })
          .then((resp) => {
            this.initialized = true;
            if (resp.error) {
              throw resp.error;
            }
            if (resp.result) {
              this.minDate = new Date(resp.result.min_date);
              this.maxDate = new Date(resp.result.max_date);

              // Set time parts to 00:00:00
              this.minDate.setHours(0, 0, 0, 0);
              this.maxDate.setHours(0, 0, 0, 0);

              if (!this.startDate) {
                this.startDate = this.minDate;
              }
              if (!this.endDate) {
                this.endDate = this.maxDate;
              }
            }
          })
          .catch((error) => {
            console.error(error);
          });
      }
    },
    modelValue(newTime, oldTime) {
      if (oldTime && newTime) {
        const oldStartTime = new Date(oldTime[0]);
        const oldEndTime = new Date(oldTime[1]);
        const newStartTime = new Date(newTime[0]);
        const newEndTime = new Date(newTime[1]);

        if (oldStartTime.getTime() !== newStartTime.getTime() && oldEndTime.getTime() !== newEndTime.getTime()) {
          this.startDate = new Date(newTime[0]);
          this.endDate = new Date(newTime[1]);
        }
      } else if (!oldTime && newTime) {
        this.startDate = new Date(newTime[0]);
        this.endDate = new Date(newTime[1]);
      }
    },
  },
  created() {
    this.debouncedUpdateFilters = utils.debounce(this.updateFilters, 600);
    this.minDate = new Date(1990, 0, 1, 0, 0, 0, 0);
    this.maxDate = new Date(this.currentYear, 0, 1, 0, 0, 0, 0);
  },
  methods: {
    updateFilters() {
      if (!this.isActive) {
        this.$emit('update:modelValue', null);
        return;
      }
      if (this.startDate || this.endDate) {
        this.$emit('update:modelValue', [
          `${this.startDate ? this.startDate : ''}`,
          `${this.endDate ? this.endDate : ''}`,
        ]);
        return;
      }
      this.$emit('update:modelValue', undefined);
    },
    handleClear() {
      if (this.minDate) {
        this.startDate = this.minDate;
      } else {
        this.startDate = null;
      }

      if (this.maxDate) {
        this.endDate = this.maxDate;
      } else {
        this.endDate = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* Modal Backdrop */
.date-filter-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

/* Modal Dialog */
.date-filter-modal {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  max-width: 900px;
  width: fit-content;
  display: flex;
  flex-direction: column;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #e0e0e0;

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }
  }

  &__close {
    background: none;
    border: none;
    font-size: 28px;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;

    &:hover {
      background-color: #f5f5f5;
      border-radius: 4px;
    }
  }

  &__content {
    padding: 20px;
    overflow-y: auto;
    max-height: 60vh;
  }

  &__footer {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    padding: 12px 20px;
    border-top: 1px solid #e0e0e0;
    background-color: #f9f9f9;
    border-radius: 0 0 8px 8px;
  }
}

/* Date Pickers Container */
.date-container {
  display: flex;
  gap: 24px;
  justify-content: center;
}

.date-picker-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-weight: 600;
    font-size: 14px;
  }
}

.date-range-loader {
  display: flex;
  gap: 24px;
  justify-content: center;
}

/* Fade Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

:deep .v-date-picker {
  box-shadow: none;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
</style>
